/**
 * TopLineItems.tsx — the "Voci principali" tab body: a Pareto view of the fewest,
 * largest line items whose combined value reaches 80% of the project's CME total.
 *
 * Decided by Omar 2026-07-28 — this replaces the >=5%-per-item rule from the
 * functional specification and restores the cumulative-Pareto reading of the
 * earlier Use Cases document. It sits alongside "Categorie & voci", which is
 * unchanged: this tab answers "which few items IS this bid?", that one answers
 * "what is everything, and what does it add up to?".
 *
 * Rows past `coveringCount` are context added to fill the list, not part of the
 * 80%. They are de-emphasised and called out in the footer — a list that silently
 * mixes the two reads as "these all matter", which is exactly the sort of quietly
 * misleading surface the 2026-07-24 de-fake pass removed elsewhere.
 *
 * Renders WITHOUT its own card wrapper: it is mounted inside UnifiedTable's card,
 * below the shared tab bar.
 *
 * Goal 55B, L-1 — the BASIS IS STATED. Items whose amount nobody read are left out of the
 * ranking (the server counts them in `unreadAmounts`), so the shares are of the read amounts
 * only and the tab says so: «Calcolato su N voci con importo letto; M escluse…». With every
 * amount unread there is nothing to rank, and the tab says that instead of «no positive items».
 */
import { type TopLineItems as TopLineItemsData } from "../../lib/tenderApi";
import { fmtEur } from "./helpers";
import { ItemBillPage, type ItemPagesView } from "./ItemBillPage";

interface Props {
  data: TopLineItemsData | undefined;
  loading: boolean;
  error: string | null;
  /** Whether the project has ANY line items — i.e. whether it has been analysed.
   * Needed because an empty Pareto list has two very different causes, and the
   * wrong message tells the user to re-run an hours-long, real-cost AI job that
   * cannot change the result. See the round-4 defect note below. */
  hasLineItems?: boolean;
  /** Goal 55B, L-1: how many of the project's items have an amount that was read. */
  readItems?: number;
  /** G3a — the bill page of each item; absent outside the Audit page (nothing is shown). */
  itemPages?: ItemPagesView;
}

const COL_SPAN = 7;

function Message({ children, tone }: { children: string; tone?: "err" }): JSX.Element {
  return (
    <tr>
      <td
        colSpan={COL_SPAN}
        style={{
          textAlign: "center",
          padding: 26,
          color: tone === "err" ? "var(--c-err)" : "var(--c-ink-3)",
        }}
      >
        {children}
      </td>
    </tr>
  );
}

export function TopLineItems({
  data,
  loading,
  error,
  hasLineItems,
  readItems,
  itemPages,
}: Props): JSX.Element {
  const items = data?.items ?? [];
  const covering = data?.coveringCount ?? 0;
  const coverage = data?.coveragePct ?? 80;
  const hasContextRows = items.length > covering;
  const unread = data?.unreadAmounts ?? 0;
  const nothingRead = unread > 0 && data?.totalCme == null;

  return (
    <>
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 44, textAlign: "right" }}>#</th>
            <th style={{ width: 90 }}>ID</th>
            <th>Descrizione</th>
            <th style={{ textAlign: "right", width: 120 }}>Quantità</th>
            <th style={{ textAlign: "right", width: 130 }}>Totale</th>
            <th style={{ textAlign: "right", width: 100 }}>% del CME</th>
            <th style={{ textAlign: "right", width: 110 }}>Cumulato</th>
          </tr>
        </thead>
        <tbody>
          {loading && <Message>Caricamento…</Message>}
          {!loading && error && <Message tone="err">{error}</Message>}
          {/* An empty Pareto list has two causes and they need opposite advice.
              Since credit lines stopped being candidates (round-4 fix), an
              ANALYSED project made entirely of credits/zero-value rows also lands
              here — and telling that user to "run the analysis" is worse than
              unhelpful: it is an hours-long, real-cost AI job that cannot change
              the outcome. Found by the independent tester, 2026-07-28. */}
          {!loading && !error && items.length === 0 && (
            <Message>
              {nothingRead
                ? `Voci principali non determinabili: nessuna delle ${unread} voci ha un importo letto.`
                : hasLineItems
                  ? "Questo progetto non presenta voci di costo positive da mostrare — controlla le voci in «Tutte le voci»."
                  : "Nessuna voce da mostrare — esegui l'analisi del progetto."}
            </Message>
          )}

          {!loading &&
            !error &&
            items.map((item, idx) => {
              const isContext = idx >= covering;
              const dim = isContext ? "var(--c-ink-3)" : undefined;
              const weight = isContext ? 400 : 600;
              return (
                <tr key={item.id}>
                  <td
                    style={{
                      textAlign: "right",
                      color: "var(--c-ink-3)",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {idx + 1}
                  </td>
                  <td style={{ color: "var(--c-ink-3)" }}>{item.code ?? "—"}</td>
                  <td style={{ color: isContext ? "var(--c-ink-2)" : undefined }}>
                    {item.description}
                    {itemPages && (
                      <span style={{ marginLeft: 8 }}>
                        <ItemBillPage itemId={item.id} pages={itemPages} />
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {item.qty == null ? "—" : item.qty.toLocaleString("it-IT")}
                    {item.unit ? ` ${item.unit}` : ""}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      fontWeight: weight,
                    }}
                  >
                    {item.total == null ? "—" : fmtEur(item.total)}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      color: dim,
                      fontWeight: weight,
                    }}
                  >
                    {item.pctBudget === null ? "—" : `${item.pctBudget.toFixed(1)}%`}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      fontVariantNumeric: "tabular-nums",
                      color: "var(--c-ink-3)",
                    }}
                  >
                    {item.cumulativePct.toFixed(1)}%
                  </td>
                </tr>
              );
            })}
        </tbody>
      </table>

      {/* Goal 55B, L-1: the basis, whenever an item was left out because nobody read it. */}
      {!loading && !error && unread > 0 && !nothingRead && (
        <div
          data-testid="pareto-basis"
          style={{
            padding: "8px 14px",
            fontSize: 12,
            color: "var(--c-warn)",
            borderTop: "1px solid var(--c-line)",
          }}
        >
          {`${
            readItems == null
              ? "Calcolato sulle sole voci con importo letto"
              : `Calcolato su ${readItems} ${readItems === 1 ? "voce" : "voci"} con importo letto`
          }; ${unread} ${
            unread === 1 ? "esclusa perché il suo importo" : "escluse perché il loro importo"
          } non è stato letto.`}
        </div>
      )}

      {/* State plainly where the 80% stops, so the context rows below it are never
          read as significance. Only shown when there are context rows at all. */}
      {!loading && !error && hasContextRows && (
        <div
          style={{
            padding: "8px 14px",
            fontSize: 12,
            color: "var(--c-ink-3)",
            borderTop: "1px solid var(--c-line)",
          }}
        >
          {covering === 0
            ? `Impossibile calcolare le voci principali per questo progetto — le prime ${items.length} voci sono mostrate come riferimento.`
            : `Le prime ${covering} voci compongono il ${coverage}% del costo totale; le restanti sono mostrate come contesto.`}
        </div>
      )}
    </>
  );
}
